import React, {FC, ReactNode, useState} from 'react';

import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {
  InterfaceEventsEnum,
  trackInterfaceEvent,
} from '../../../../libs/shared/core/src/services/tracking/tracking';
import {
  bannerSuccessDelay,
  makeBannerUid,
} from '../../../../libs/shared/core/src/store/banners/bannerHelpers';
import {showBannerWithAutomaticRemovalAction} from '../../../../libs/shared/core/src/store/banners/commonBannerActions';
import {BannerTypesEnum} from '../../../../libs/shared/core/src/store/banners/commonBannerState';
import {SuccessBannerTypesEnum} from '../../../../libs/shared/core/src/store/banners/commonBannerTypes';
import {Button} from '../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ButtonGroup} from '../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroup';
import {ButtonGroupSpacer} from '../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroupSpacer';
import {Dialog} from '../../../../libs/shared/ui/src/components/dialogs/dialog';
import {DialogButtonGroup} from '../../../../libs/shared/ui/src/components/dialogs/dialogButtonGroup';
import {DialogContent} from '../../../../libs/shared/ui/src/components/dialogs/dialogContent';
import {DialogHeader} from '../../../../libs/shared/ui/src/components/dialogs/dialogHeader';
import {useAppDispatch} from '../../../helpers/appReduxHelpers';
import {
  navigateToFirstTeamAction,
  togglePinSandboxTeamAsyncAction,
} from '../../../store/data/teammates/teammatesActions';

/*
 * Props.
 */

interface LeaveSandboxCoordinatorProps {
  children: (props: LeaveSandboxCoordinatorRenderProps) => ReactNode;
}

interface LeaveSandboxCoordinatorRenderProps {
  isConfirmLeaveSandboxOpen: boolean;
  onLeaveSandboxClick: () => void;
}

/*
 * Component.
 */

export const LeaveSandboxCoordinator: FC<LeaveSandboxCoordinatorProps> = (props) => {
  const dispatch = useAppDispatch();

  const [isConfirmLeaveSandboxOpen, setIsConfirmLeaveSandboxOpen] = useState(false);

  const onLeaveSandboxClick = () => {
    setIsConfirmLeaveSandboxOpen(true);
  };

  const onConfirmLeaveSandboxClick = async () => {
    setIsConfirmLeaveSandboxOpen(false);

    // Fire and forget, because we're redirecting elsewhere.
    dispatch(togglePinSandboxTeamAsyncAction(false));

    dispatch(navigateToFirstTeamAction());

    const now = Date.now();
    dispatch(
      showBannerWithAutomaticRemovalAction({
        uid: makeBannerUid(),
        type: BannerTypesEnum.SUCCESS,
        successType: SuccessBannerTypesEnum.LEFT_SANDBOX,
        createdAt: now,
        expiresAt: now + bannerSuccessDelay,
      }),
    );

    trackInterfaceEvent(InterfaceEventsEnum.LEAVE_SANDBOX_CONFIRM_CLICK);
  };

  return (
    <>
      {props.children({isConfirmLeaveSandboxOpen, onLeaveSandboxClick})}

      {isConfirmLeaveSandboxOpen && (
        <Dialog onRequestClose={() => setIsConfirmLeaveSandboxOpen(false)}>
          <DialogHeader title={<Trans id="sandbox-indicator-remove-title">Leave sandbox workspace?</Trans>} />
          <DialogContent>
            <Trans id="sandbox-indicator-remove-content-confirm">
              This will remove your access to the sandbox workspace and all the demo inboxes.
            </Trans>
          </DialogContent>
          <DialogButtonGroup>
            <ButtonGroupSpacer />
            <ButtonGroup>
              <Button onClick={() => setIsConfirmLeaveSandboxOpen(false)}>
                <Trans id="sandbox-indicator-remove-cancel">Cancel</Trans>
              </Button>
              <Button variant={ButtonVariantsEnum.PRIMARY} onClick={onConfirmLeaveSandboxClick}>
                <Trans id="sandbox-indicator-remove-ok">Leave</Trans>
              </Button>
            </ButtonGroup>
          </DialogButtonGroup>
        </Dialog>
      )}
    </>
  );
};
